import { useState } from "react"
import { useTranslation } from "react-i18next"
import { CalendarBlank, CalendarX } from "@phosphor-icons/react"
import { CheckedState } from "@radix-ui/react-checkbox"
import { Button } from "@/components/ui/button"
import { SingleCheckbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog"
import { ListRadioGroup, ListRadioItem } from "@/components/ui/list"
import { useMedicalRecordDialogStore } from "@/modules/patients/detail/store/useCloseMedicalRecordDialogStore"

type CloseMedialRecordConfirmationDialogProps = {
  onCloseMedicalRecord: (option: CloseOutput) => void
}

type CloseOption = "KEEP" | "CANCEL"

export type CloseOutput = {
  decision: CloseOption
  shouldIncreaseMissedCall: boolean
}

type ConfirmationOption = {
  value: CloseOption
  text: string
  subtext: string
  icon: React.JSX.Element
}

const CloseMedialRecordConfirmationDialog = ({ onCloseMedicalRecord }: CloseMedialRecordConfirmationDialogProps) => {
  const [t] = useTranslation()
  const [closeDecision, setCloseDecision] = useState<CloseOption>("KEEP")
  const shouldIncreaseMissedCall = useMedicalRecordDialogStore((state) => state.shouldIncreaseMissedCall)
  const setShouldIncreaseMissedCall = useMedicalRecordDialogStore((state) => state.setShouldIncreaseMissedCall)

  const onChangeCloseAppointmentType = (value: CloseOption) => {
    setCloseDecision(value)
  }

  const confirmationOptions: Array<ConfirmationOption> = [
    {
      value: "KEEP",
      text: t("patient.closeAppointmentDialog.keepTeleconsultation"),
      subtext: t("patient.closeAppointmentDialog.keepTeleconsultationDescription"),
      icon: <CalendarBlank />,
    },
    {
      value: "CANCEL",
      text: t("patient.closeAppointmentDialog.cancelTeleconsultation"),
      subtext: t("patient.closeAppointmentDialog.cancelTeleconsultationDescription"),
      icon: <CalendarX />,
    },
  ]

  const openCloseConfirmationDialog = useMedicalRecordDialogStore((state) => state.openCloseConfirmationDialog)
  const setCloseConfirmationDialog = useMedicalRecordDialogStore((state) => state.setCloseConfirmationDialog)

  return (
    <Dialog open={openCloseConfirmationDialog} onOpenChange={setCloseConfirmationDialog}>
      <DialogPortal>
        <DialogOverlay onMouseDown={(e) => e.stopPropagation()} />
        <DialogContent
          onPointerDownOutside={(e) => e.preventDefault()}
          onMouseDown={(e) => e.stopPropagation()}
          onOpenAutoFocus={(e) => e.preventDefault()}
          variant="small"
          data-testid="confirmation-dialog-content">
          <DialogHeader>
            <DialogTitle data-testid="confirmation-dialog-title">
              {t("patient.closeAppointmentDialog.close")}
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-2 px-6 py-2">
            <ListRadioGroup className="gap-4" defaultValue={closeDecision} onValueChange={onChangeCloseAppointmentType}>
              {confirmationOptions.map((entry) => (
                <ListRadioItem
                  {...entry}
                  key={entry.value}
                  id={entry.value}
                  className="p-0 py-1 transition-all"
                  data-testid={`confirmation-dialog-option-${entry.value}`}>
                  <div className="text-on-surface-subdued">{entry.icon}</div>
                  <div className="text-left">
                    <div className="text-on-surface text-sm leading-5">{entry.text}</div>
                    <div className="text-on-surface-subdued text-xs leading-4 tracking-[0.25px]">{entry.subtext}</div>
                  </div>
                </ListRadioItem>
              ))}
              <label className="flex justify-between gap-4">
                <span className="text-on-surface px-1 pt-4 text-sm leading-5">
                  {t("patient.closeAppointmentDialog.shouldIncreaseMissedCall")}
                </span>
                <SingleCheckbox
                  className="pt-1"
                  checked={shouldIncreaseMissedCall}
                  onCheckedChange={(value: CheckedState) => setShouldIncreaseMissedCall(value === true)}
                  data-testid="missed-call-attempt"
                />
              </label>
            </ListRadioGroup>
          </div>

          <DialogFooter className="flex gap-2 p-6 pt-4">
            <Button
              variant="secondary"
              onClick={() => setCloseConfirmationDialog(false)}
              data-testid="keep-confirmation-dialog">
              {t("patient.closeAppointmentDialog.continueEditing")}
            </Button>

            <Button
              data-testid="close-confirmation-dialog"
              onClick={() => onCloseMedicalRecord({ decision: closeDecision, shouldIncreaseMissedCall })}>
              {t("patient.closeAppointmentDialog.closeMedicalRecord")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export { CloseMedialRecordConfirmationDialog }
